export const ROLE_LABELS = {
  owner: 'Owner',
  dm_creative: 'Design Manager — Creative',
  dm_technical: 'Design Manager — Technical',
  dm_architecture: 'Design Manager — Architecture',
  operations: 'Operations',
  accounts: 'Accounts',
  commercials: 'Commercials',
  hr: 'HR',
  team: 'Team Member',
};

export const ROLE_SHORT = {
  owner: 'Owner',
  dm_creative: 'DM — Creative',
  dm_technical: 'DM — Technical',
  dm_architecture: 'DM — Architecture',
  operations: 'Ops',
  accounts: 'Accounts',
  commercials: 'Commercials',
  hr: 'HR',
  team: 'Team',
};

// Role IDs in the order they should appear in dropdowns.
export const ALL_ROLE_IDS = [
  'owner', 'dm_creative', 'dm_technical', 'dm_architecture',
  'operations', 'accounts', 'commercials', 'hr', 'team',
];

// The three design-lead peers (same access level).
export const DM_ROLES = ['dm_creative', 'dm_technical', 'dm_architecture'];
export const MANAGER_ROLES = ['owner', ...DM_ROLES];

// Fixed set for the Department dropdown.
export const DEPARTMENTS = [
  'Leadership', 'Design', 'Technical', 'Architecture',
  'Operations', 'Accounts', 'Commercials', 'People', 'HR',
];

// Fixed status enum used across the app.
export const USER_STATUSES = ['active', 'pending', 'inactive'];
export const PROJECT_STATUSES = ['active', 'on_hold', 'closed'];
export const MILESTONE_STATUSES = ['pending', 'in_progress', 'done', 'invoiced', 'paid'];

export const canSeeAmounts = (role) => ['owner', 'accounts', 'commercials', 'operations'].includes(role);
export const canSeeBillingName = (role) => ['owner', 'accounts', 'commercials'].includes(role);
export const canExecuteInvoice = (role) => ['owner', 'accounts'].includes(role);
export const canSeePendingInvoices = (role) => ['owner', 'accounts', 'commercials'].includes(role);
export const canSeeQuotes = (role) => ['owner', 'accounts', 'commercials', 'operations'].includes(role);
export const canSeeVisits = (role) => ['owner', 'operations', 'accounts', 'commercials', 'dm_creative', 'dm_technical', 'dm_architecture', 'team'].includes(role);
export const canEditKnowledge = (role) => ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'operations'].includes(role);
export const isOwner = (role) => role === 'owner';
export const isManager = (role) => MANAGER_ROLES.includes(role);
export const isOps = (role) => role === 'operations';
export const canUploadWO = (role) => role === 'owner' || role === 'operations';
// Accounts must see Work Orders (all financial rows). Uploading a WO PDF stays
// Ops's job, but viewing / entering money values / approving are Accounts's
// core responsibilities — the backend `WO_VIEW_ROLES` already allows this;
// the frontend just needs to stop hiding the nav + page from them.
export const canSeeWOs = (role) => ['owner', 'operations', 'accounts'].includes(role);
export const canSeeAudit = (role) => role === 'owner' || role === 'operations';
export const canManageTeam = (role) => MANAGER_ROLES.includes(role);

export const STAGES = [
  { key: 'advance_retainer', label: 'Advance / Retainer' },
  { key: 'strategy_concept', label: 'Strategy & Concept' },
  { key: 'schematic', label: 'Schematic' },
  { key: 'dd', label: 'DD' },
  { key: 'tdp', label: 'Tender Docs' },
  { key: 'construction_admin_closure', label: 'CA & Closure' },
];
export const STAGE_LABEL = Object.fromEntries(STAGES.map(s => [s.key, s.label]));
export const DEFAULT_STAGE = 'strategy_concept';
const STAGE_KEYS = new Set(STAGES.map(s => s.key));

// Legacy pre-v3 aliases + safety mapper. Anything unknown falls back to the
// default so pages never crash on newly-created (stage-less) projects.
const STAGE_ALIAS_MAP = {
  intake: 'strategy_concept',
  strategy: 'strategy_concept',
  concept: 'strategy_concept',
  advance: 'advance_retainer',
  retainer: 'advance_retainer',
  'advance/retainer': 'advance_retainer',
  construction_admin: 'construction_admin_closure',
  project_closure: 'construction_admin_closure',
  ca: 'construction_admin_closure',
  closure: 'construction_admin_closure',
  design_development: 'dd',
};

export function normalizeStage(raw) {
  if (!raw) return DEFAULT_STAGE;
  const s = String(raw).trim().toLowerCase();
  if (STAGE_KEYS.has(s)) return s;
  return STAGE_ALIAS_MAP[s] || DEFAULT_STAGE;
}

// Best-guess label for any incoming string (including junk or missing).
export function stageLabelSafe(raw) {
  return STAGE_LABEL[normalizeStage(raw)];
}
